import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Leaderboard from "@/pages/Leaderboard";
import MealPlanning from "@/pages/MealPlanning";
import Goals from "@/pages/Goals";
import GroupGoals from "@/pages/GroupGoals";
import WeightTracking from "@/pages/WeightTracking";
import Profile from "@/pages/Profile";
import Workouts from "@/pages/Workouts";
import Shop from "@/pages/Shop";
import Progress from "@/pages/Progress";
import Leveling from "@/pages/Leveling";
import Privacy from "@/pages/Privacy";
import { LevelUpModal, BadgeEarnedModal } from "@/components/CelebrationModals";

// Create a context for celebration events
export const CelebrationContext = createContext();

export const useCelebration = () => useContext(CelebrationContext);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);

  // Celebration modal states
  const [levelUpData, setLevelUpData] = useState(null);
  const [badgeData, setBadgeData] = useState(null);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Celebration trigger functions
  const triggerLevelUp = (levelData, xp) => {
    setLevelUpData({ ...levelData, xp });
    setShowLevelUp(true);
  };

  const triggerBadgeEarned = (badge) => {
    setBadgeData(badge);
    setShowBadge(true);
  };

  const celebrationValue = {
    triggerLevelUp,
    triggerBadgeEarned
  };

  const ProtectedRoute = ({ children }) => {
    return token ? children : <Navigate to="/login" />;
  };

  return (
    <CelebrationContext.Provider value={celebrationValue}>
      <BrowserRouter>
        <div className="App min-h-screen bg-[#F5F3EF]">
          <main>
            <Routes>
              <Route path="/" element={!token ? <Landing /> : <Navigate to="/dashboard" />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/login" element={<Login setToken={setToken} setUser={setUser} />} />
              <Route path="/register" element={<Register setToken={setToken} setUser={setUser} />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard user={user} /></ProtectedRoute>} />
              <Route path="/workouts" element={<ProtectedRoute><Workouts /></ProtectedRoute>} />
              <Route path="/meals" element={<ProtectedRoute><MealPlanning /></ProtectedRoute>} />
              <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
              <Route path="/groups" element={<ProtectedRoute><GroupGoals user={user} /></ProtectedRoute>} />
              <Route path="/weight" element={<ProtectedRoute><WeightTracking /></ProtectedRoute>} />
              <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard user={user} /></ProtectedRoute>} />
              <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
              <Route path="/levels" element={<ProtectedRoute><Leveling /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile user={user} /></ProtectedRoute>} />
            </Routes>
          </main>
          <Toaster position="top-right" />

          {/* Celebration Modals */}
          <LevelUpModal
            open={showLevelUp}
            onClose={() => setShowLevelUp(false)}
            levelData={levelUpData}
            xp={levelUpData?.xp}
          />
          <BadgeEarnedModal
            open={showBadge}
            onClose={() => setShowBadge(false)}
            badge={badgeData}
          />
        </div>
      </BrowserRouter>
    </CelebrationContext.Provider>
  );
}

export default App;
